<template>
  <v-dialog v-model="model" fullscreen hide-overlay persistent>
    <!-- Fullscreen Dialog using pure Vuetify classes -->
    <v-card tile class="d-flex flex-column fill-height company-form-dialog-card">
      <v-card-title class="primary" elevation="2">
        <v-icon color="white" class="mr-3">mdi-domain-plus</v-icon>
        <v-toolbar-title>
          <div>
            <span class="text-h6 font-weight-medium white--text">{{ formTitle }}</span>
            <div class="body-2 opacity-80" v-if="editedItem.id">
              ID: {{ editedItem.id }}
            </div>
          </div>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <circle-button @click="closeDialog" icon="mdi-close" color="primary" />
      </v-card-title>

      <!-- Main Content using Vuetify flex and overflow classes -->
      <div class="flex-grow-1 overflow-y-auto grey lighten-4">
        <v-container fluid class="pa-6">
          <v-row>
            <!-- Left Column - Company Settings & Information -->
            <v-col cols="12" xl="6" lg="6" class="pr-lg-3">
              <!-- Company Settings Section -->
              <v-form ref="companyForm" v-model="valid">
                <company-settings
                  v-model="editedItem"
                  :companies="companies"
                  class="mb-4"
                />
                <!-- Contact Information Section -->
                <contact-information v-model="editedItem" class="mb-4" />
              </v-form>
            </v-col>

            <!-- Right Column - Contact & Shift Management -->
            <v-col cols="12" xl="6" lg="6" class="pl-lg-3">
              <company-type v-model="editedItem" class="mb-4" />
              <!-- Shift Management Section -->
              <shift-management
                v-if="editedItem?.contract !== 4"
                v-model="editedItem"
                class="mb-4"
              />

              <slot></slot>
            </v-col>
          </v-row>

          <!-- Mobile scroll hint using Vuetify utilities -->
          <v-row v-if="$vuetify.breakpoint.smAndDown" class="mt-4">
            <v-col cols="12">
              <v-alert
                color="success"
                icon="mdi-information-outline"
                outlined
                dense
                class="text-center ma-0"
              >
                <span class="caption">
                  {{ $t("scroll_up_review_sections") }}
                </span>
              </v-alert>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- Footer using Vuetify flex utilities -->
      <v-card-actions class="flex-grow-0 px-4 white elevation-8">
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog">
          {{ $t("cancel") }}
        </v-btn>
        <primary-button
          @click="saveCompany"
          :loading="isSaving"
          :disabled="isSaving || !valid"
          :dark="valid && !isSaving"
        >
          <v-icon left>mdi-content-save</v-icon>
          {{ $t("save") }}
        </primary-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import companyService from "@/services/company";
import ContactInformation from "./sections/ContactInformation.vue";
import CompanySettings from "./sections/CompanySettings.vue";
import ShiftManagement from "./sections/ShiftManagement.vue";
import CompanyType from "./sections/CompanyType.vue";
import hasModel from "@/mixins/hasModel";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "CompanyFormDialog",
  mixins: [hasModel, hasValidation, hasSnackbar],
  components: {
    ContactInformation,
    ShiftManagement,
    CompanySettings,
    CompanyType,
  },
  props: {
    editedItem: {
      type: Object,
      required: true,
    },
    companies: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isSaving: false,
      valid: false,
    };
  },
  computed: {
    formTitle() {
      return this.editedItem?.id
        ? [this.editedItem.name, this.$t("change_company")].join(" - ")
        : this.$t("new_company");
    },
  },
  methods: {
    closeDialog() {
      this.$emit("close");
    },
    async saveCompany() {
      if (this.isSaving) return;

      // Validate form before saving
      if (!this.$refs.companyForm.validate()) {
        this.showErrorSnackbar(this.$t("please_fill_required_fields"));
        return;
      }

      this.isSaving = true;

      try {
        // Prepare the item data
        const itemData = { ...this.editedItem };
        itemData.parent_id = itemData.parent ? itemData.parent.id : 0;

        if (this.editedItem.id) {
          // Update existing company (has ID)
          await companyService.updateCompany(itemData.id, itemData);

          this.showSuccessSnackbar(this.$t("company_updated"));

          // Emit success event with update data
          this.$emit("company-updated", {
            item: itemData,
          });
        } else {
          // Create new company (no ID)
          const response = await companyService.createCompany(itemData);
          itemData.id = response.data.id;

          this.showSuccessSnackbar(this.$t("company_created"));

          // Emit success event with new data
          this.$emit("company-created", {
            item: itemData,
          });
        }

        // Close the dialog
        this.closeDialog();
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_saving_company"));
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/styles/_variables.scss";

/* Minimal Company Form Dialog Styles using Vuetify 2 utilities */
.company-form-dialog-card {
  .section-container {
    background: white;
    margin-bottom: 1px;
  }

  .subsection {
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    margin: 24px;
  }

  /* Custom brand color for inputs */
  .clean-input ::v-deep .v-input__slot:hover {
    border-color: $primary-color !important;
  }

  .clean-input ::v-deep .v-input--is-focused .v-input__slot {
    border-color: $primary-color !important;
    box-shadow: 0 0 0 2px $primary-lighten !important;
  }
}
</style>
